import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
	Box,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	Chip,
	Paper,
	CircularProgress,
	Alert,
	Autocomplete,
	TextField,
	LinearProgress,
	IconButton,
	Collapse,
} from '@mui/material';
import {
	KeyboardArrowDown as ExpandIcon,
	KeyboardArrowUp as CollapseIcon,
} from '@mui/icons-material';
import { apiService } from '../services/api';

// ── Types ────────────────────────────────────────────────────────────────────

interface RecordTypeInsightProps {
	snapshotId: string;
}

interface RecordTypeRow {
	id: string;
	parent_object_api_name: string;
	parent_object_label: string | null;
	record_type_api_name: string;
	record_type_full_name: string;
	namespace: string | null;
	label: string | null;
	is_active: boolean;
	description: string | null;
	record_count: number | null;
	object_record_count: number | null;
	[key: string]: unknown;
}

type UsageLevel = 'unused' | 'rarely_used' | 'lightly_used' | 'moderately_used' | 'heavily_used';

interface ObjectSummary {
	objectApiName: string;
	objectLabel: string | null;
	recordTypeCount: number;
	activeCount: number;
	inactiveCount: number;
	managedCount: number;
	customCount: number;
	unusedCount: number;
	totalRecords: number | null;
	recordTypes: RecordTypeRow[];
}

type SummarySort = 'objectLabel' | 'objectApiName' | 'recordTypeCount' | 'activeCount' | 'totalRecords';
type DetailSort = 'label' | 'record_type_api_name' | 'is_active' | 'namespace' | 'record_count' | 'pct' | 'usage_level';
type SortDir = 'asc' | 'desc';

// ── Helpers ──────────────────────────────────────────────────────────────────

function comparePrimitive(a: unknown, b: unknown, dir: SortDir): number {
	if (a === null || a === undefined) a = dir === 'asc' ? Infinity : -Infinity;
	if (b === null || b === undefined) b = dir === 'asc' ? Infinity : -Infinity;

	if (typeof a === 'string' && typeof b === 'string') {
		return dir === 'asc' ? a.localeCompare(b) : b.localeCompare(a);
	}
	if (typeof a === 'boolean' && typeof b === 'boolean') {
		return dir === 'asc' ? Number(a) - Number(b) : Number(b) - Number(a);
	}
	const numA = Number(a);
	const numB = Number(b);
	return dir === 'asc' ? numA - numB : numB - numA;
}

function formatCount(value: number | null | undefined): string {
	if (value === null || value === undefined) return '\u2014';
	return value.toLocaleString();
}

function formatPct(recordCount: number | null, objectTotal: number | null): string {
	if (recordCount === null || objectTotal === null || objectTotal === 0) return '\u2014';
	return `${((recordCount / objectTotal) * 100).toFixed(1)}%`;
}

function getPct(recordCount: number | null, objectTotal: number | null): number | null {
	if (recordCount === null || objectTotal === null || objectTotal === 0) return null;
	return (recordCount / objectTotal) * 100;
}

function getUsageLevel(recordCount: number | null, objectTotal: number | null): UsageLevel | null {
	if (recordCount === null) return null;
	if (recordCount === 0) return 'unused';
	const pct = objectTotal && objectTotal > 0 ? (recordCount / objectTotal) * 100 : null;
	if (recordCount <= 3 || (pct !== null && pct < 1)) return 'rarely_used';
	if (pct !== null && pct < 5) return 'lightly_used';
	if (pct !== null && pct < 25) return 'moderately_used';
	return 'heavily_used';
}

const USAGE_LEVEL_ORDER: Record<UsageLevel, number> = {
	unused: 0,
	rarely_used: 1,
	lightly_used: 2,
	moderately_used: 3,
	heavily_used: 4,
};

const USAGE_LEVEL_CONFIG: Record<UsageLevel, { label: string; color: 'default' | 'warning' | 'info' | 'primary' | 'success' }> = {
	unused: { label: 'Unused', color: 'default' },
	rarely_used: { label: 'Rarely Used', color: 'warning' },
	lightly_used: { label: 'Lightly Used', color: 'info' },
	moderately_used: { label: 'Moderately Used', color: 'primary' },
	heavily_used: { label: 'Heavily Used', color: 'success' },
};

// ── Expandable object row ────────────────────────────────────────────────────

const ObjectDetailTable: React.FC<{
	recordTypes: RecordTypeRow[];
	hasRecordData: boolean;
}> = ({ recordTypes, hasRecordData }) => {
	const [sortField, setSortField] = useState<DetailSort>('record_count');
	const [sortDir, setSortDir] = useState<SortDir>('desc');

	const sorted = useMemo(() => {
		return [...recordTypes].sort((a, b) => {
			if (sortField === 'pct') {
				return comparePrimitive(
					getPct(a.record_count, a.object_record_count),
					getPct(b.record_count, b.object_record_count),
					sortDir,
				);
			}
			if (sortField === 'usage_level') {
				const aLevel = getUsageLevel(a.record_count, a.object_record_count);
				const bLevel = getUsageLevel(b.record_count, b.object_record_count);
				return comparePrimitive(
					aLevel !== null ? USAGE_LEVEL_ORDER[aLevel] : null,
					bLevel !== null ? USAGE_LEVEL_ORDER[bLevel] : null,
					sortDir,
				);
			}
			return comparePrimitive(a[sortField], b[sortField], sortDir);
		});
	}, [recordTypes, sortField, sortDir]);

	const handleSort = (field: DetailSort) => {
		if (field === sortField) {
			setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
		} else {
			setSortField(field);
			const textFields: DetailSort[] = ['record_type_api_name', 'label', 'namespace'];
			setSortDir(textFields.includes(field) ? 'asc' : 'desc');
		}
	};

	type DetailColumn = { field: DetailSort; label: string; align?: 'left' | 'right'; show?: boolean };
	const columns: DetailColumn[] = [
		{ field: 'label', label: 'Label' },
		{ field: 'record_type_api_name', label: 'API Name' },
		{ field: 'is_active', label: 'Active' },
		{ field: 'usage_level', label: 'Usage', show: hasRecordData },
		{ field: 'namespace', label: 'Namespace' },
		{ field: 'record_count', label: 'Records', align: 'right', show: hasRecordData },
		{ field: 'pct', label: '% of Object', align: 'right', show: hasRecordData },
	];
	const visibleColumns = columns.filter(c => c.show !== false);

	return (
		<Table size="small">
			<TableHead>
				<TableRow>
					{visibleColumns.map(col => (
						<TableCell key={col.field} align={col.align || 'left'} sx={{ fontWeight: 600 }}>
							<TableSortLabel
								active={sortField === col.field}
								direction={sortField === col.field ? sortDir : 'asc'}
								onClick={() => handleSort(col.field)}
							>
								{col.label}
							</TableSortLabel>
						</TableCell>
					))}
					<TableCell sx={{ fontWeight: 600, minWidth: 350 }}>Description</TableCell>
				</TableRow>
			</TableHead>
			<TableBody>
			{sorted.map(rt => {
				const pctValue = getPct(rt.record_count, rt.object_record_count);
				const usageLevel = getUsageLevel(rt.record_count, rt.object_record_count);
				return (
					<TableRow key={rt.id} hover>
						<TableCell>{rt.label || '\u2014'}</TableCell>
						<TableCell>
							<Typography variant="body2" sx={{ fontWeight: 500, fontFamily: 'monospace', fontSize: '0.8rem' }}>
								{rt.record_type_api_name}
							</Typography>
						</TableCell>
						<TableCell>
							{rt.is_active ? (
								<Chip label="Active" size="small" color="success" variant="outlined" sx={{ height: 22, fontSize: '0.75rem' }} />
							) : (
								<Chip label="Inactive" size="small" color="default" variant="outlined" sx={{ height: 22, fontSize: '0.75rem' }} />
							)}
						</TableCell>
						{hasRecordData && (
							<TableCell>
								{usageLevel !== null ? (
									<Chip
										label={USAGE_LEVEL_CONFIG[usageLevel].label}
										size="small"
										color={USAGE_LEVEL_CONFIG[usageLevel].color}
										variant="outlined"
										sx={{ height: 22, fontSize: '0.75rem' }}
									/>
								) : '\u2014'}
							</TableCell>
						)}
						<TableCell>
							{rt.namespace ? (
								<Chip label={rt.namespace} size="small" color="info" variant="outlined" sx={{ height: 22, fontSize: '0.75rem' }} />
							) : '\u2014'}
						</TableCell>
						{hasRecordData && (
							<>
								<TableCell align="right">
									<Typography
										variant="body2"
										sx={{
											fontWeight: 600,
											color: rt.record_count != null && rt.record_count > 0
												? 'success.main'
												: rt.record_count === 0 ? 'text.secondary' : undefined,
										}}
									>
										{formatCount(rt.record_count)}
									</Typography>
								</TableCell>
								<TableCell align="right">
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
										{pctValue !== null && (
											<LinearProgress
												variant="determinate"
												value={Math.min(pctValue, 100)}
												sx={{ width: 50, height: 6, borderRadius: 3 }}
											/>
										)}
										<Typography variant="body2" sx={{ minWidth: 45, textAlign: 'right' }}>
											{formatPct(rt.record_count, rt.object_record_count)}
										</Typography>
									</Box>
								</TableCell>
							</>
						)}
						<TableCell>
							<Typography variant="caption" color="text.secondary">
								{rt.description || '\u2014'}
							</Typography>
						</TableCell>
					</TableRow>
				);
			})}
			</TableBody>
		</Table>
	);
};

// ── Main component ───────────────────────────────────────────────────────────

type ChipFilter = 'active' | 'inactive' | 'managed' | 'unused' | 'rarely_used' | 'lightly_used' | null;

function matchesChipFilter(rt: RecordTypeRow, filter: ChipFilter): boolean {
	if (!filter) return true;
	switch (filter) {
		case 'active': return rt.is_active;
		case 'inactive': return !rt.is_active;
		case 'managed': return rt.namespace !== null;
		case 'unused': return rt.is_active && rt.record_count === 0;
		case 'rarely_used': {
			const level = getUsageLevel(rt.record_count, rt.object_record_count);
			return rt.is_active && level === 'rarely_used';
		}
		case 'lightly_used': {
			const level = getUsageLevel(rt.record_count, rt.object_record_count);
			return rt.is_active && level === 'lightly_used';
		}
	}
}

export const RecordTypeInsight: React.FC<RecordTypeInsightProps> = ({ snapshotId }) => {
	const [recordTypes, setRecordTypes] = useState<RecordTypeRow[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [objectFilter, setObjectFilter] = useState<string | null>(null);
	const [chipFilter, setChipFilter] = useState<ChipFilter>(null);

	// Summary-level sort
	const [summarySort, setSummarySort] = useState<SummarySort>('recordTypeCount');
	const [summarySortDir, setSummarySortDir] = useState<SortDir>('desc');

	// Expanded object rows
	const [expandedObjects, setExpandedObjects] = useState<Set<string>>(new Set());

	const toggleChipFilter = (filter: ChipFilter) => {
		setChipFilter(prev => prev === filter ? null : filter);
	};

	const loadRecordTypes = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const runsResponse = await apiService.getAnalysisRuns(snapshotId);
			if (!runsResponse.success || !runsResponse.data) {
				setError(runsResponse.error || 'Failed to load analysis runs');
				return;
			}

			const completedRuns = runsResponse.data.runs
				.filter((r) => r.status === 'completed' || r.status === 'completed_with_errors')
				.sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime());

			if (completedRuns.length === 0) {
				setError('No completed analysis runs found. Run an analysis first from the Analysis page.');
				return;
			}

			const resultsResponse = await apiService.getAnalysisResults(completedRuns[0].run_id, 'record_types');
			if (!resultsResponse.success || !resultsResponse.data) {
				setError(resultsResponse.error || 'Failed to load record type results');
				return;
			}

			setRecordTypes(resultsResponse.data.results as RecordTypeRow[]);
		} catch (err) {
			setError((err as Error).message);
		} finally {
			setLoading(false);
		}
	}, [snapshotId]);

	useEffect(() => {
		loadRecordTypes();
	}, [loadRecordTypes]);

	// Build object summaries
	const objectSummaries = useMemo<ObjectSummary[]>(() => {
		const map = new Map<string, RecordTypeRow[]>();
		for (const rt of recordTypes) {
			const existing = map.get(rt.parent_object_api_name) || [];
			existing.push(rt);
			map.set(rt.parent_object_api_name, existing);
		}

		return Array.from(map.entries()).map(([objectApiName, rts]) => ({
			objectApiName,
			objectLabel: rts[0]?.parent_object_label ?? null,
			recordTypeCount: rts.length,
			activeCount: rts.filter(r => r.is_active).length,
			inactiveCount: rts.filter(r => !r.is_active).length,
			managedCount: rts.filter(r => r.namespace !== null).length,
			customCount: rts.filter(r => r.namespace === null).length,
			unusedCount: rts.filter(r => r.is_active && r.record_count === 0).length,
			totalRecords: rts[0]?.object_record_count ?? null,
			recordTypes: rts,
		}));
	}, [recordTypes]);

	// Filtered + sorted summaries
	const filteredSummaries = useMemo(() => {
		let list = objectSummaries;
		if (objectFilter) {
			list = list.filter(s => s.objectApiName === objectFilter);
		}
		if (chipFilter) {
			list = list
				.map(s => {
					const filtered = s.recordTypes.filter(rt => matchesChipFilter(rt, chipFilter));
					if (filtered.length === 0) return null;
					return {
						...s,
						recordTypes: filtered,
						recordTypeCount: filtered.length,
						activeCount: filtered.filter(r => r.is_active).length,
						inactiveCount: filtered.filter(r => !r.is_active).length,
						managedCount: filtered.filter(r => r.namespace !== null).length,
						customCount: filtered.filter(r => r.namespace === null).length,
						unusedCount: filtered.filter(r => r.is_active && r.record_count === 0).length,
					};
				})
				.filter((s): s is ObjectSummary => s !== null);
		}
		return [...list].sort((a, b) => comparePrimitive(
			a[summarySort],
			b[summarySort],
			summarySortDir,
		));
	}, [objectSummaries, objectFilter, chipFilter, summarySort, summarySortDir]);

	// Available object names for filter autocomplete
	const objectOptions = useMemo(
		() => objectSummaries
			.map(s => ({ apiName: s.objectApiName, label: s.objectLabel }))
			.sort((a, b) => (a.label || a.apiName).localeCompare(b.label || b.apiName)),
		[objectSummaries],
	);

	const handleSummarySort = (field: SummarySort) => {
		if (field === summarySort) {
			setSummarySortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
		} else {
			setSummarySort(field);
			setSummarySortDir(field === 'objectLabel' || field === 'objectApiName' ? 'asc' : 'desc');
		}
	};

	const toggleExpand = (objectApiName: string) => {
		setExpandedObjects(prev => {
			const next = new Set(prev);
			if (next.has(objectApiName)) {
				next.delete(objectApiName);
			} else {
				next.add(objectApiName);
			}
			return next;
		});
	};

	// Aggregate stats
	const totalRecordTypes = recordTypes.length;
	const totalActive = recordTypes.filter(r => r.is_active).length;
	const totalInactive = totalRecordTypes - totalActive;
	const totalManaged = recordTypes.filter(r => r.namespace !== null).length;
	const objectCount = objectSummaries.length;
	const hasRecordData = recordTypes.some(r => r.record_count !== null);
	const totalUnused = hasRecordData ? recordTypes.filter(r => r.is_active && r.record_count === 0).length : 0;
	const totalRarelyUsed = hasRecordData ? recordTypes.filter(r => {
		const level = getUsageLevel(r.record_count, r.object_record_count);
		return r.is_active && level === 'rarely_used';
	}).length : 0;
	const totalLightlyUsed = hasRecordData ? recordTypes.filter(r => {
		const level = getUsageLevel(r.record_count, r.object_record_count);
		return r.is_active && level === 'lightly_used';
	}).length : 0;

	if (loading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
				<CircularProgress />
			</Box>
		);
	}

	if (error) {
		return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
	}

	if (recordTypes.length === 0) {
		return (
			<Alert severity="info" sx={{ m: 2 }}>
				No record type data found in this analysis run.
			</Alert>
		);
	}

	type SummaryColumn = { field: SummarySort; label: string; align?: 'left' | 'right' };
	const summaryColumns: SummaryColumn[] = [
		{ field: 'objectLabel', label: 'Object' },
		{ field: 'recordTypeCount', label: 'Record Types', align: 'right' },
		{ field: 'activeCount', label: 'Active', align: 'right' },
		{ field: 'totalRecords', label: 'Total Records', align: 'right' },
	];

	return (
		<Box>
			<Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
				Record Types Overview
			</Typography>

			{/* Summary chips — clickable to filter */}
			<Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
				<Chip
					label={`${totalRecordTypes} Record Types`}
					variant="outlined"
					onClick={chipFilter ? () => setChipFilter(null) : undefined}
					sx={chipFilter ? { cursor: 'pointer' } : undefined}
				/>
				<Chip label={`${objectCount} Objects`} variant="outlined" color="primary" />
				<Chip
					label={`${totalActive} Active`}
					variant={chipFilter === 'active' ? 'filled' : 'outlined'}
					color="success"
					onClick={() => toggleChipFilter('active')}
					sx={{ cursor: 'pointer' }}
				/>
				{totalInactive > 0 && (
					<Chip
						label={`${totalInactive} Inactive`}
						variant={chipFilter === 'inactive' ? 'filled' : 'outlined'}
						color="warning"
						onClick={() => toggleChipFilter('inactive')}
						sx={{ cursor: 'pointer' }}
					/>
				)}
				{totalManaged > 0 && (
					<Chip
						label={`${totalManaged} Managed`}
						variant={chipFilter === 'managed' ? 'filled' : 'outlined'}
						color="info"
						onClick={() => toggleChipFilter('managed')}
						sx={{ cursor: 'pointer' }}
					/>
				)}
				{hasRecordData && totalUnused > 0 && (
					<Chip
						label={`${totalUnused} Unused (Active)`}
						variant={chipFilter === 'unused' ? 'filled' : 'outlined'}
						color="error"
						onClick={() => toggleChipFilter('unused')}
						sx={{ cursor: 'pointer' }}
					/>
				)}
				{hasRecordData && totalRarelyUsed > 0 && (
					<Chip
						label={`${totalRarelyUsed} Rarely Used`}
						variant={chipFilter === 'rarely_used' ? 'filled' : 'outlined'}
						color="warning"
						onClick={() => toggleChipFilter('rarely_used')}
						sx={{ cursor: 'pointer' }}
					/>
				)}
				{hasRecordData && totalLightlyUsed > 0 && (
					<Chip
						label={`${totalLightlyUsed} Lightly Used`}
						variant={chipFilter === 'lightly_used' ? 'filled' : 'outlined'}
						color="info"
						onClick={() => toggleChipFilter('lightly_used')}
						sx={{ cursor: 'pointer' }}
					/>
				)}
			</Box>

			{!hasRecordData && (
				<Alert severity="info" sx={{ mb: 2 }}>
					Record count data is not available for this analysis run. Connect a Salesforce org with live access to populate record counts and percentages.
				</Alert>
			)}

			{/* Object filter */}
			<Box sx={{ mb: 2, maxWidth: 400 }}>
				<Autocomplete
					options={objectOptions}
					getOptionLabel={(opt) => opt.label ? `${opt.label} (${opt.apiName})` : opt.apiName}
					value={objectOptions.find(o => o.apiName === objectFilter) ?? null}
					onChange={(_e, val) => setObjectFilter(val?.apiName ?? null)}
					isOptionEqualToValue={/* istanbul ignore next -- called internally by MUI Autocomplete to compare selected value with options list; not reachable via RTL user events */ (opt, val) => opt.apiName === val.apiName}
					renderInput={(params) => (
						<TextField {...params} label="Filter by Object" size="small" placeholder="All Objects" />
					)}
					clearOnEscape
				/>
			</Box>

			{/* Summary table with expandable rows */}
			<Paper variant="outlined">
				<TableContainer>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell sx={{ width: 48 }} />
								{summaryColumns.map(col => (
									<TableCell key={col.field} align={col.align || 'left'} sx={{ fontWeight: 600 }}>
										<TableSortLabel
											active={summarySort === col.field}
											direction={summarySort === col.field ? summarySortDir : 'asc'}
											onClick={() => handleSummarySort(col.field)}
										>
											{col.label}
										</TableSortLabel>
									</TableCell>
								))}
								<TableCell align="right" sx={{ fontWeight: 600 }}>Inactive</TableCell>
								{hasRecordData && <TableCell align="right" sx={{ fontWeight: 600 }}>Unused</TableCell>}
								<TableCell align="right" sx={{ fontWeight: 600 }}>Managed</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{filteredSummaries.map(summary => {
								const isExpanded = expandedObjects.has(summary.objectApiName);
								return (
									<React.Fragment key={summary.objectApiName}>
										<TableRow
											hover
											sx={{ cursor: 'pointer', '& > *': { borderBottom: isExpanded ? 'none !important' : undefined } }}
											onClick={() => toggleExpand(summary.objectApiName)}
										>
											<TableCell sx={{ width: 48, p: 0, pl: 1 }}>
												<IconButton size="small">
													{isExpanded ? <CollapseIcon /> : <ExpandIcon />}
												</IconButton>
											</TableCell>
										<TableCell>
											{summary.objectLabel ? (
												<Box>
													<Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
														{summary.objectLabel}
													</Typography>
													<Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
														{summary.objectApiName}
													</Typography>
												</Box>
											) : (
												<Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.85rem' }}>
													{summary.objectApiName}
												</Typography>
											)}
										</TableCell>
											<TableCell align="right">
												<Typography variant="body2" sx={{ fontWeight: 600 }}>
													{summary.recordTypeCount}
												</Typography>
											</TableCell>
											<TableCell align="right">
												<Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
													{summary.activeCount}
												</Typography>
											</TableCell>
											<TableCell align="right">
												{formatCount(summary.totalRecords)}
											</TableCell>
											<TableCell align="right">
												{summary.inactiveCount > 0 ? (
													<Chip
														label={summary.inactiveCount}
														size="small"
														color="warning"
														variant="outlined"
														sx={{ height: 22, fontSize: '0.75rem' }}
													/>
												) : (
													<Typography variant="body2" color="text.secondary">0</Typography>
												)}
											</TableCell>
											{hasRecordData && (
												<TableCell align="right">
													{summary.unusedCount > 0 ? (
														<Chip
															label={summary.unusedCount}
															size="small"
															color="error"
															variant="outlined"
															sx={{ height: 22, fontSize: '0.75rem' }}
														/>
													) : (
														<Typography variant="body2" color="text.secondary">0</Typography>
													)}
												</TableCell>
											)}
											<TableCell align="right">
												{summary.managedCount > 0 ? (
													<Chip
														label={summary.managedCount}
														size="small"
														color="info"
														variant="outlined"
														sx={{ height: 22, fontSize: '0.75rem' }}
													/>
												) : (
													<Typography variant="body2" color="text.secondary">0</Typography>
												)}
											</TableCell>
										</TableRow>
										<TableRow>
											<TableCell colSpan={hasRecordData ? 8 : 7} sx={{ p: 0 }}>
												<Collapse in={isExpanded} timeout="auto" unmountOnExit>
													<Box sx={{ pl: 6, pr: 2, py: 1, backgroundColor: 'grey.50' }}>
														<ObjectDetailTable
															recordTypes={summary.recordTypes}
															hasRecordData={hasRecordData}
														/>
													</Box>
												</Collapse>
											</TableCell>
										</TableRow>
									</React.Fragment>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			</Paper>
		</Box>
	);
};
